import { Field } from '@atlaskit/form';
import { Stack, Text } from '@atlaskit/primitives';
import FieldPicker from '@databases/field-picker/FieldPicker';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils, type DefaultRootState } from '@databases/redux';
import {
  addChangeToDraft,
  type AddChangeToDraftOptions
} from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  type BrokenDatabase,
  type DatabaseField,
  DatabaseFieldType,
  type Database
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { getReferencedEntryDetailsField } from '@databases/utils/database/field-types/entryDetails';
import {
  getDatabaseFieldOptional,
  getDatabaseFields,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type EntryDetailsFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.ENTRY_DETAILS>;
};

const fieldSettingsAnalyticsOptions = {
  fieldSettingsAnalytics: {
    source: 'fieldContextualMenu',
    fieldType: DatabaseFieldType.ENTRY_DETAILS
  }
} as AddChangeToDraftOptions;

const allowedSourceFieldTypes = [
  DatabaseFieldType.BACKLINK,
  DatabaseFieldType.ENTRY_LINK
];

const isEntryDetailsSourceField = (
  field: DatabaseField
): field is
  | DatabaseField<DatabaseFieldType.BACKLINK>
  | DatabaseField<DatabaseFieldType.ENTRY_LINK> => {
  return allowedSourceFieldTypes.includes(field.type);
};

const selectReferencedDatabase = (
  state: DefaultRootState,
  field:
    | DatabaseField<DatabaseFieldType.BACKLINK>
    | DatabaseField<DatabaseFieldType.ENTRY_LINK>
    | undefined
): Database | BrokenDatabase | undefined => {
  if (!field) {
    return undefined;
  }

  if (field.settings.databaseSpace?.key && field.settings.databaseId) {
    return selectDatabaseByIdOptional(state, {
      spaceKey: field.settings.databaseSpace.key,
      id: field.settings.databaseId
    });
  }

  if (field.settings.contentId) {
    return selectDatabaseByContentIdOptional(state, {
      contentId: field.settings.contentId
    });
  }

  return undefined;
};

const EntryDetailsFieldSettings: FunctionComponent<EntryDetailsFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const [sourceField, sourceFields, valueField, valueFields, isValuePickerDisabled] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: true
      });
      const databaseDraftWithoutDeleted = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: false
      });
      const selectedSourceField = getReferencedEntryDetailsField({
        database: databaseDraft,
        field
      });
      const referencedDatabase = selectReferencedDatabase(state, selectedSourceField);
      const referencedDatabaseDraft =
        referencedDatabase &&
        !isBrokenDatabase(referencedDatabase) &&
        selectDatabaseDraftByContentId(state, {
          contentId: referencedDatabase.contentId,
          includeDeleted: false
        });

      return [
        selectedSourceField,
        getDatabaseFields(databaseDraftWithoutDeleted).filter(isEntryDetailsSourceField),
        referencedDatabaseDraft
          ? getDatabaseFieldOptional(
              referencedDatabaseDraft,
              field.settings.lookupFieldId
            )
          : undefined,
        referencedDatabaseDraft ? getDatabaseFields(referencedDatabaseDraft) : undefined,
        !referencedDatabaseDraft
      ] as const;
    });

  const handleSourceFieldChange = useCallback(
    (value: DatabaseField | undefined) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_ID,
            fieldId: field.id,
            settingsFieldId: value?.id
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleValueFieldChange = useCallback(
    (value: DatabaseField | undefined) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_ENTRY_FIELD_FIELD,
            fieldId: field.id,
            entryFieldField: value?.id
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id]
  );

  useEffect(() => {
    if (sourceField?.settings.contentId) {
      void dispatch(loadDatabaseByContentId(sourceField.settings.contentId));
    }
  }, [dispatch, sourceField?.settings.contentId]);

  return (
    <Stack space="space.050">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.entryDetailsDisplaySourceLabel)}
          </Text>
        }
        name="entry-details-source-field"
        isRequired
      >
        {({ fieldProps }) => (
          <FieldPicker
            fields={sourceFields}
            value={sourceField}
            onChange={handleSourceFieldChange}
            isRequired
            placeholder={formatMessage(
              intlMessages['field-settings.type-entry-link-selector-placeholder']
            )}
            spacing="compact"
            inputId={fieldProps.id}
            autoSelectFirst
          />
        )}
      </Field>

      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.entryDetailsDisplayedLabel)}
          </Text>
        }
        name="entry-details-value-field"
        isRequired
      >
        {({ fieldProps }) => (
          <FieldPicker
            fields={valueFields}
            value={valueField}
            onChange={handleValueFieldChange}
            isRequired
            isDisabled={isValuePickerDisabled}
            placeholder={formatMessage(messages.entryDetailsValuePlaceholder)}
            spacing="compact"
            inputId={fieldProps.id}
          />
        )}
      </Field>
    </Stack>
  );
};

export default EntryDetailsFieldSettings;
